// Iter 220 — P2: Betway team alias confirmation widget.
//
// Surfaces fuzzy matches (similarity 60-94%) the matcher could not
// auto-confirm, and lets the user say "Yes / No, these are the same
// team" via `/api/betway/aliases/confirm`.  Confirmed aliases unlock
// future placements that would otherwise fail with "match not found".
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CheckCircle2, XCircle, Sparkles, RefreshCw } from "lucide-react";

export default function AliasReviewWidget() {
  const [loading, setLoading] = useState(false);
  const [needsReview, setNeedsReview] = useState([]);
  const [autoConfirmed, setAutoConfirmed] = useState(0);
  const [working, setWorking] = useState(null);

  const fetchReview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/betway/aliases/backfill`, {
        auto_confirm_threshold: 0.95,
        min_review_score: 0.60,
        dry_run: true,
      });
      setNeedsReview(data.needs_review || []);
      setAutoConfirmed((data.auto_confirmed || []).length);
    } catch (err) {
      // Most users will get 503 (cache empty) — that's expected outside CA region
      const msg = err?.response?.data?.detail || "";
      if (msg.toLowerCase().includes("cache empty")) {
        toast.info("Betway availability cache still warming — try again in a few minutes");
      } else {
        toast.error("Failed to load alias review");
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReview(); }, []);

  const confirm = async (entry, same) => {
    setWorking(`${entry.source_name}::${entry.betway_name}`);
    try {
      await axios.post(`${API}/betway/aliases/confirm`, {
        source_name: entry.source_name,
        betway_name: entry.betway_name,
        same,
      });
      toast.success(same ? "Marked as same team" : "Marked as different");
      // Optimistically remove the row
      setNeedsReview((cur) => cur.filter((e) =>
        !(e.source_name === entry.source_name && e.betway_name === entry.betway_name)
      ));
    } catch (err) {
      toast.error("Failed to save");
    } finally { setWorking(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card/40 p-5" data-testid="alias-review-widget">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-fuchsia-400" />
        <div className="font-display text-base font-bold tracking-tight">Teach the Betway matcher</div>
        <Badge variant="outline" className="text-[10px]">{needsReview.length}</Badge>
        <button
          onClick={fetchReview}
          disabled={loading}
          data-testid="alias-review-refresh"
          className="ml-auto text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          title="Re-scan"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {autoConfirmed > 0 && (
        <div className="text-[11px] text-emerald-300 mb-2" data-testid="alias-auto-confirmed">
          ✓ {autoConfirmed} alias{autoConfirmed === 1 ? "" : "es"} auto-matched at ≥95% confidence
        </div>
      )}

      {loading && needsReview.length === 0 && (
        <div className="text-xs text-muted-foreground">Scanning…</div>
      )}

      {!loading && needsReview.length === 0 && (
        <div className="text-xs text-muted-foreground" data-testid="alias-empty">
          Nothing to review right now — every team in your predictions resolves to a Betway listing.
        </div>
      )}

      <div className="space-y-2">
        {needsReview.slice(0, 12).map((entry) => {
          const key = `${entry.source_name}::${entry.betway_name}`;
          const busy = working === key;
          return (
            <div
              key={key}
              className="flex items-center gap-3 text-xs rounded-md border border-border/60 bg-background/40 px-3 py-2"
              data-testid={`alias-row-${entry.source_name.replace(/\s/g, '-')}`}
            >
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">
                  <span className="text-foreground">{entry.source_name}</span>
                  <span className="mx-2 opacity-50">≟</span>
                  <span className="text-foreground">{entry.betway_name}</span>
                </div>
                <div className="text-muted-foreground">
                  {entry.league} · similarity {(entry.score * 100).toFixed(0)}%
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => confirm(entry, true)}
                disabled={busy}
                data-testid={`alias-confirm-yes-${entry.source_name.replace(/\s/g, '-')}`}
                className="bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                Same
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => confirm(entry, false)}
                disabled={busy}
                data-testid={`alias-confirm-no-${entry.source_name.replace(/\s/g, '-')}`}
                className="text-rose-300 border-rose-500/40 hover:bg-rose-500/10"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" />
                Different
              </Button>
            </div>
          );
        })}
        {needsReview.length > 12 && (
          <div className="text-[11px] text-muted-foreground text-center pt-1">
            … {needsReview.length - 12} more — confirm the top ones first
          </div>
        )}
      </div>
    </div>
  );
}
